import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ScreenDetectionContext, ScreenSize } from '../screenDetection/screenDetection';
import { getLsItem, setLsItem } from '../storage/localStorageService';
import calcWidthFromCharCount from './calcWidthFromCharCount';
import { orderWithNewColumn } from './columnOrder';
import fitColumns, { ColumnMinimums, measureColumnMinimums } from './fitColumns';
import foldXLargeLayouts from './foldXLargeLayouts';
import PlaylistColumns, {
  ColumnScope,
  ColumnScreenSize,
  columnScreenSize,
  ColumnSetting,
  ColumnSettings,
  ColumnSettingsStore,
  SmallScreenColumns,
  TrackProperty,
} from './playlistColumns';
import playlistColumnSubset from './playlistColumnsSubset';

export interface IColumnSettingsContext {
  /**
   * Column widths as they are to be shown right now: the saved layouts,
   * fitted to the viewport each scope last reported. Read this; nothing
   * outside this service needs the layouts as saved.
   */
  columnSettingsStore: ColumnSettingsStore;
  /** The narrowest each column may go, per scope. */
  minimums: { [S in ColumnScope]?: ColumnMinimums };
  /**
   * Tells the service how wide a scope's viewport is, less the play and
   * menu buttons and the scrollbar. Called by whatever renders the scope,
   * on mount and on every resize; the widths above follow.
   */
  setViewportWidth(scope: ColumnScope, width: number): void;
  setColumnWidth(columnName: TrackProperty, width: number, scope: ColumnScope): void;
  /**
   * The columns' order, first to last, for the current screen size. Keys
   * not in the list keep their place number; keys not in the scope are
   * ignored. What View options writes when a column is moved.
   */
  setColumnOrder(scope: ColumnScope, order: TrackProperty[]): void;
  // /**
  //  * Allows the user to choose per-playlist which columnthe list is sorted by
  //  *
  //  * We already allow for permanent sorting by column so this isn't stricly needed.
  //  * Maybe a nice to have, if the current implementation works it sets a global default.
  //  * Needs to be per-playlist
  //  *
  //  * @param columnName
  //  * @param scope
  //  */
  // setColumnDefault(
  //   columnName: TrackProperty | 'index',
  //   scope: ColumnScope,
  // ): void;
  /**
   * Grows or shrinks the target column in an attempt
   * to match the viewport width
   */
  columnAutoResize(
    columName: TrackProperty,
    viewportWidth: number,
    scope: ColumnScope,
    contentMinimumWidth: number,
  ): void;
  /**
   * Creates a scope using default columns with default width.
   *
   * @param viewportWidth
   */
  initializeScope(viewportWidth: number, scope: ColumnScope): Partial<ColumnSettings>;
}

export const ColumnSettingsContext = React.createContext({} as IColumnSettingsContext);

/**
 * Slots in columns that did not exist when the user last saved their settings.
 *
 * The store is keyed by scope and screen size and read back verbatim -
 * initializeScope only runs for a scope with no settings at all - so without
 * this a column added to PlaylistColumns would never appear for anyone who
 * has used the app before. A hidden column is stored as width 0, so a key
 * that is missing outright really does mean "did not exist yet".
 *
 * New columns take their width off the widest flexible column rather than
 * widening the table past the viewport. A new column that is not sized from a
 * character count cannot be measured without a viewport, so it lands at width
 * 0 (hidden) and the user can unhide it; every column added so far is sized
 * from a character count.
 *
 * The order is the user's (setColumnOrder, from View options) and is not
 * touched for columns already stored; it used to be rewritten from the
 * table on every load, back when nothing else authored it. A new column is
 * placed by orderWithNewColumn - after the nearest table predecessor the
 * user has - and the whole order is renumbered from zero, so numbers stay
 * whole and unique whatever the table's are.
 */
function addMissingColumns(store: ColumnSettingsStore) {
  const tableOrder = (Object.keys(PlaylistColumns) as TrackProperty[]).sort(
    (a, b) => PlaylistColumns[a].sortOrder - PlaylistColumns[b].sortOrder,
  );

  for (const scopeKey in ColumnScope) {
    const scope = scopeKey as ColumnScope;

    for (const sizeKey in store[scope]) {
      // Whatever keys the stored data has, which for a user who has not
      // been folded yet still includes X-Large. Running before the fold
      // is deliberate: the layout that wins the fold has its new columns
      // already in it.
      const screenSize = sizeKey as ColumnScreenSize;
      const settings = store[scope][screenSize];

      if (!settings) continue;

      const isSmall = screenSize === ScreenSize.Small || screenSize === ScreenSize.Medium;

      const missing = (Object.keys(PlaylistColumns) as TrackProperty[])
        // Not truthiness: -1 is a column that exists here but starts
        // hidden, and it has to be written in so the Columns menu can
        // offer it. Only 0 means "not in this scope".
        .filter(
          (col) =>
            PlaylistColumns[col].defaultWidthPerc[scope] !== 0 &&
            settings[col] === undefined &&
            (!isSmall || SmallScreenColumns[screenSize as ScreenSize.Small].includes(col)),
        );

      if (missing.length === 0) continue;

      let order = (Object.keys(settings) as TrackProperty[])
        .filter((col) => PlaylistColumns[col])
        .sort((a, b) => settings[a]!.sortOrder - settings[b]!.sortOrder);

      for (const col of missing) {
        const width = PlaylistColumns[col].calcFromCharCount
          ? calcWidthFromCharCount(PlaylistColumns[col].defaultWidthPerc[scope])
          : 0;

        const donor = (Object.keys(settings) as TrackProperty[])
          .filter(
            (c) =>
              PlaylistColumns[c] &&
              !PlaylistColumns[c].calcFromCharCount &&
              settings[c]!.width > width * 2,
          )
          .sort((a, b) => settings[b]!.width - settings[a]!.width)[0];

        if (width && donor) {
          settings[donor]!.width -= width;
        }

        order = orderWithNewColumn(order, tableOrder, col);

        settings[col] = {
          width,
          sortOrder: 0,
        };
      }

      order.forEach((col, index) => {
        settings[col]!.sortOrder = index;
      });
    }
  }

  return store;
}

interface Props {
  children: React.ReactNode;
}

export default function ColumnSettingsService({ children }: Props) {
  console.debug('Column settings rendering');

  const detectedScreenSize = useContext(ScreenDetectionContext);
  const screenSize = columnScreenSize(detectedScreenSize);

  // Which screen the user was on when the app started, for the one-time
  // fold below - which has to choose between two layouts and picks the
  // one being looked at. A ref because the fold runs once, on mount.
  const screenSizeAtStartup = useRef(detectedScreenSize);

  /**
   * The layouts as saved: pixels, and the viewport width they were set in.
   * Everything handed out is derived from this and `viewportWidths`.
   */
  const [columnSettingsStore, setColumnSettingsStore] = useState<ColumnSettingsStore>(
    () => new ColumnSettingsStore(),
  );
  /** What each scope on screen last reported as its viewport width. */
  const [viewportWidths, setViewportWidths] = useState<{ [S in ColumnScope]?: number }>({});

  // The viewport reports arrive from a ResizeObserver, on every frame of
  // a resize, and must not re-subscribe on every write to the store - so
  // setViewportWidth reads the store through a ref rather than closing
  // over it.
  const storeRef = useRef(columnSettingsStore);
  storeRef.current = columnSettingsStore;

  const minimums = useMemo(() => {
    const perScope: { [S in ColumnScope]?: ColumnMinimums } = {};

    for (const scope in ColumnScope) {
      perScope[scope as ColumnScope] = measureColumnMinimums(scope as ColumnScope);
    }

    return perScope;
  }, []);

  useEffect(function loadLocalState() {
    const version = getLsItem('columnSettingsVersion');

    if (version !== undefined && +version === 1) {
      const stored = getLsItem('columnSettings');

      // We currently do not persist defaults when used and unchanged,
      // not really any point is there
      if (stored !== undefined) {
        const store = addMissingColumns(new ColumnSettingsStore(JSON.parse(stored)));

        // A fold is persisted at once rather than left for the next
        // write - see foldXLargeLayouts.
        if (foldXLargeLayouts(store, screenSizeAtStartup.current)) {
          setLsItem('columnSettings', store);
        }

        setColumnSettingsStore(store);
      }
    } else {
      const store = new ColumnSettingsStore();

      setLsItem('columnSettings', store);
      setColumnSettingsStore(store);
      setLsItem('columnSettingsVersion', 1);
    }
  }, []);

  /**
   * Persists a layout for the current screen size, with the viewport width
   * it now describes.
   */
  const writeScope = useCallback(
    (scope: ColumnScope, settings: Partial<ColumnSettings>, referenceWidth: number | undefined) => {
      const updatedState = new ColumnSettingsStore({
        ...columnSettingsStore,
        [scope]: {
          ...columnSettingsStore[scope],
          [screenSize]: settings,
        },
        referenceWidths: {
          ...columnSettingsStore.referenceWidths,
          [scope]: {
            ...columnSettingsStore.referenceWidths[scope],
            [screenSize]: referenceWidth,
          },
        },
      });

      setLsItem('columnSettings', updatedState);
      setColumnSettingsStore(updatedState);

      return updatedState;
    },
    [columnSettingsStore, screenSize],
  );

  /**
   * A scope's layout as it is shown right now, and the viewport width that
   * makes it so. Every change the user makes starts from this rather than
   * from the saved pixels: they are dragging the column they can see, so
   * what they see becomes the new saved layout, at the current width.
   */
  const shown = useCallback(
    (scope: ColumnScope) => {
      const saved = columnSettingsStore[scope][screenSize];
      const viewportWidth = viewportWidths[scope];
      const referenceWidth = columnSettingsStore.referenceWidths[scope]?.[screenSize];

      if (saved === undefined || viewportWidth === undefined) {
        return { settings: saved, referenceWidth };
      }

      return {
        settings: fitColumns(saved, referenceWidth, viewportWidth, minimums[scope]!),
        referenceWidth: viewportWidth,
      };
    },
    [columnSettingsStore, screenSize, viewportWidths, minimums],
  );

  /**
   * What everything renders from. Identity is preserved for every scope
   * whose fit is a no-op, so a resize that changes nothing re-renders
   * nothing.
   */
  const fittedStore = useMemo(() => {
    let fitted: ColumnSettingsStore | undefined;

    for (const scopeKey in ColumnScope) {
      const scope = scopeKey as ColumnScope;
      const saved = columnSettingsStore[scope][screenSize];
      const viewportWidth = viewportWidths[scope];

      if (saved === undefined || viewportWidth === undefined) continue;

      const settings = fitColumns(
        saved,
        columnSettingsStore.referenceWidths[scope]?.[screenSize],
        viewportWidth,
        minimums[scope]!,
      );

      if (settings !== saved) {
        fitted = fitted || new ColumnSettingsStore(columnSettingsStore);
        fitted[scope] = { ...fitted[scope], [screenSize]: settings };
      }
    }

    return fitted || columnSettingsStore;
  }, [columnSettingsStore, screenSize, viewportWidths, minimums]);

  const setViewportWidth = useCallback(
    (scope: ColumnScope, width: number) => {
      setViewportWidths((prev) =>
        prev[scope] !== undefined && Math.abs(prev[scope]! - width) < 0.5
          ? prev
          : { ...prev, [scope]: width },
      );

      // A layout saved before references existed: the viewport that first
      // shows it is what it was made for, as far as anyone can tell. Written
      // once; from then on it is fitted like any other.
      const store = storeRef.current;

      if (
        store[scope][screenSize] !== undefined &&
        store.referenceWidths[scope]?.[screenSize] === undefined
      ) {
        const adopted = new ColumnSettingsStore({
          ...store,
          referenceWidths: {
            ...store.referenceWidths,
            [scope]: { ...store.referenceWidths[scope], [screenSize]: width },
          },
        });

        storeRef.current = adopted;
        setLsItem('columnSettings', adopted);
        setColumnSettingsStore(adopted);
      }
    },
    [screenSize],
  );

  const setColumnWidth = useCallback(
    (columnName: TrackProperty, width: number, scope: ColumnScope) => {
      const { settings, referenceWidth } = shown(scope);

      const prevColumnSetting: ColumnSetting = settings?.[columnName] || ({} as ColumnSetting);

      writeScope(
        scope,
        {
          ...settings,
          [columnName]: {
            ...prevColumnSetting,
            width,
          },
        },
        referenceWidth,
      );
    },
    [shown, writeScope],
  );

  const setColumnOrder = useCallback(
    (scope: ColumnScope, order: TrackProperty[]) => {
      const { settings, referenceWidth } = shown(scope);

      if (!settings) {
        return;
      }

      const next: Partial<ColumnSettings> = { ...settings };

      order.forEach((columnName, index) => {
        const setting = next[columnName];

        if (setting) {
          next[columnName] = { ...setting, sortOrder: index };
        }
      });

      writeScope(scope, next, referenceWidth);
    },
    [shown, writeScope],
  );

  // const setColumnDefault = useCallback(function setColumnDefault(
  //   columnName: TrackProperty | 'index',
  //   scope: ColumnScope,
  // ) {
  //   const columnSettings = columnSettingsStore[scope][screenSize];

  //   if (columnSettings) {
  //     for (let col in columnSettings) {
  //       const trackProp = col as TrackProperty;
  //       if (trackProp === columnName) {
  //         columnSettings[trackProp]!.default = true;
  //       }
  //       else {
  //         columnSettings[trackProp]!.default = undefined;
  //       }
  //     }
  //   }

  //   const updatedState = {
  //     ...columnSettingsStore,
  //     [scope]: {
  //       ...columnSettingsStore[scope],
  //       [screenSize]: {
  //         ...columnSettingsStore[scope][screenSize],
  //       },
  //     },
  //   };
  //   setLsItem('columnSettings', updatedState);
  //   setColumnSettingsStore(updatedState);

  // }, [columnSettingsStore, screenSize]);

  const columnAutoResize = useCallback(
    (
      columnName: TrackProperty,
      viewportWidth: number,
      scope: ColumnScope,
      contentMinimumWidth: number,
    ) => {
      const { settings: columnSettings, referenceWidth } = shown(scope);
      const currentWidth = columnSettings && columnSettings[columnName]?.width;

      if (columnSettings && currentWidth !== undefined) {
        const sum = Object.keys(columnSettings).reduce(
          (prev, cur) => prev + columnSettings[cur as TrackProperty]!.width,
          0,
        );

        if (sum > viewportWidth) {
          var targetWidth = Math.max(contentMinimumWidth, currentWidth - (sum - viewportWidth));
        } else if (sum < viewportWidth) {
          var targetWidth = currentWidth + (viewportWidth - sum);
        } else {
          // Unhiding a column sets to minimum width when width is full
          if (columnSettings[columnName]?.width === 0) {
            var targetWidth = contentMinimumWidth;
          } else {
            return;
          }
        }

        writeScope(
          scope,
          {
            ...columnSettings,
            [columnName]: {
              ...columnSettings[columnName]!,
              width: targetWidth,
            },
          },
          referenceWidth,
        );
      }
    },
    [shown, writeScope],
  );

  const initializeScope = useCallback(
    function (viewportWidth: number, scope: ColumnScope) {
      const displayedColumnKeys = Object.keys(PlaylistColumns).filter(
        (key) => PlaylistColumns[key as TrackProperty].defaultWidthPerc[scope] !== 0,
      );

      // The defaults describe this viewport, so it is the reference; the
      // character-count columns are taken off the top before the
      // percentages are applied.
      const referenceWidth = viewportWidth;

      // smaller screen sizes
      if (screenSize === ScreenSize.Small || screenSize === ScreenSize.Medium) {
        var columnSettings = playlistColumnSubset(
          viewportWidth,
          scope,
          screenSize,
          displayedColumnKeys,
        );
      } else /*(screenSize === ScreenSize.Large)*/ {
        var columnSettings = displayedColumnKeys
          .filter((colKey) => PlaylistColumns[colKey as TrackProperty].calcFromCharCount)
          .reduce((prev, cur) => {
            const curKey = cur as TrackProperty;
            const charCount = PlaylistColumns[curKey].defaultWidthPerc[scope];

            const colWidth = calcWidthFromCharCount(charCount);
            prev[curKey] = {
              width: colWidth,
              sortOrder: PlaylistColumns[curKey].sortOrder,
            };

            viewportWidth -= colWidth;
            return prev;
          }, {} as Partial<ColumnSettings>);

        displayedColumnKeys
          .filter((colKey) => PlaylistColumns[colKey as TrackProperty].calcFromCharCount !== true)
          .reduce((prev, cur) => {
            const curKey = cur as TrackProperty;

            prev[curKey] = {
              // Math.max, for the -1 columns: they exist in the scope but
              // start hidden, and a negative percentage of the viewport is
              // not a width.
              width: Math.max(
                0,
                (viewportWidth * PlaylistColumns[curKey].defaultWidthPerc[scope]) / 100,
              ),
              sortOrder: PlaylistColumns[curKey].sortOrder,
            };

            return prev;
          }, columnSettings);
      }

      writeScope(scope, columnSettings, referenceWidth);

      return columnSettings;
    },
    [writeScope, screenSize],
  );

  const ctxValue = useMemo(
    () => ({
      columnSettingsStore: fittedStore,
      minimums,
      setViewportWidth,
      setColumnWidth,
      setColumnOrder,
      columnAutoResize,
      initializeScope,
    }),
    [
      fittedStore,
      minimums,
      setViewportWidth,
      columnAutoResize,
      initializeScope,
      setColumnWidth,
      setColumnOrder,
    ],
  );

  return (
    <ColumnSettingsContext.Provider value={ctxValue}>{children}</ColumnSettingsContext.Provider>
  );
}
